import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import OTPVerification from './OTPVerification';
import GoogleOneTap from './GoogleOneTap';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import {
  PageContainer,
  BrandingSection,
  LogoContainer,
  BrandingContent,
  FeatureList,
  FeatureItem,
  FormSection,
  FormCard,
  SignInContainer,
  SignUpContainer,
  StyledForm,
  StyledTitle,
  StyledInput,
  StyledButton,
  GhostButton,
  OverlayContainer,
  Overlay,
  LeftOverlayPanel,
  RightOverlayPanel,
  StyledParagraph,
  ErrorMessage,
  LoadingSpinner,
  OverlayTitle,
  ThemeToggle
} from './StyledComponents';

const StyledAuth = () => {
  const [signingIn, setSigningIn] = useState(true);
  const [step, setStep] = useState(1); // 1: Auth forms, 2: OTP delivery selection, 3: OTP verification
  const [forgotPasswordStep, setForgotPasswordStep] = useState(1); // 1: identifier entry, 2: OTP delivery, 3: OTP verification, 4: new password
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('authDarkMode');
    return saved ? JSON.parse(saved) : false;
  });
  const [loginFormData, setLoginFormData] = useState({ email: '', phoneNumber: '', password: '' });
  const [signupFormData, setSignupFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    password: '',
    confirmPassword: ''
  });
  // Forgot password state
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordPhone, setForgotPasswordPhone] = useState('');
  const [forgotPasswordOTP, setForgotPasswordOTP] = useState(['', '', '', '', '', '']);
  const [forgotPasswordNewPassword, setForgotPasswordNewPassword] = useState('');
  const [forgotPasswordConfirmPassword, setForgotPasswordConfirmPassword] = useState('');
  const [forgotPasswordDeliveryMethod, setForgotPasswordDeliveryMethod] = useState('email');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordError, setForgotPasswordError] = useState('');
  const [showForgotPasswordNewPassword, setShowForgotPasswordNewPassword] = useState(false);
  const [showForgotPasswordConfirmPassword, setShowForgotPasswordConfirmPassword] = useState(false);
  const [isForgotPasswordMode, setIsForgotPasswordMode] = useState(false);
  
  const [loginLoading, setLoginLoading] = useState(false);
  const [signupLoading, setSignupLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [signupError, setSignupError] = useState('');
  const [signupValidationErrors, setSignupValidationErrors] = useState({});
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { login, register, sendOTP, forgotPasswordRequest, sendPasswordResetOTP, resetPassword } = useAuth();
  const navigate = useNavigate();

  const toggleDarkMode = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    localStorage.setItem('authDarkMode', JSON.stringify(newMode));
  };

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validatePassword = (password) => {
    return password.length >= 6;
  };

  const toggleForm = () => {
    setSigningIn(!signingIn);
    setLoginError('');
    setSignupError('');
  };

  const handleLoginChange = (e) => {
    const { name, value } = e.target;
    setLoginFormData(prev => ({ ...prev, [name]: value }));
    setLoginError('');
  };

  const handleSignupChange = (e) => {
    const { name, value } = e.target;
    setSignupFormData(prev => ({ ...prev, [name]: value }));
    setSignupError('');
    
    if (signupValidationErrors[name]) {
      setSignupValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateSignupForm = () => {
    const errors = {};
    
    if (!signupFormData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }
    if (!signupFormData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }
    if (!signupFormData.email) {
      errors.email = 'Email is required';
    } else if (!validateEmail(signupFormData.email)) {
      errors.email = 'Please enter a valid email';
    }
    if (!signupFormData.password) {
      errors.password = 'Password is required';
    } else if (!validatePassword(signupFormData.password)) {
      errors.password = 'Password must be at least 6 characters';
    }
    if (signupFormData.password !== signupFormData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    setSignupValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');

    // Check if either email or phone number is provided
    const hasEmail = loginFormData.email && loginFormData.email.trim();
    const hasPhone = loginFormData.phoneNumber && loginFormData.phoneNumber.trim();
    
    if (!hasEmail && !hasPhone) {
      setLoginError('Please enter either email or phone number');
      setLoginLoading(false);
      return;
    }

    // If email is provided, validate it
    if (hasEmail && !validateEmail(loginFormData.email)) {
      setLoginError('Please enter a valid email address');
      setLoginLoading(false);
      return;
    }

    if (!loginFormData.password || !validatePassword(loginFormData.password)) {
      setLoginError('Password must be at least 6 characters');
      setLoginLoading(false);
      return;
    }

    // Prepare credentials: use the filled field as identifier
    const credentials = {
      email: hasEmail ? loginFormData.email : loginFormData.phoneNumber,
      password: loginFormData.password
    };

    try {
      const result = await login(credentials);
      
      if (result && result.success) {
        toast.success('Login successful!');
        if (result.user && result.user.role === 'admin') {
          navigate('/admin/dashboard', { replace: true });
        } else if (result.user && result.user.role === 'greeter') {
          navigate('/greeter/dashboard', { replace: true });
        } else if (result.user && result.user.role === 'finance_operator') {
          navigate('/finance/dashboard', { replace: true });
        } else {
          navigate('/user/dashboard', { replace: true });
        }
      } else {
        setLoginError(result?.message || 'Login failed. Please check your credentials.');
      }
    } catch (error) {
      console.error('Login error:', error);
      setLoginError('An error occurred. Please try again.');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateSignupForm()) {
      return;
    }

    setSignupLoading(true);
    setSignupError('');

    try {
      const { confirmPassword, ...userData } = signupFormData;
      const result = await register(userData);
      
      if (result.success) {
        // Move to OTP delivery selection screen (step 2)
        setStep(2);
      } else {
        setSignupError(result.message || 'Registration failed. Please try again.');
      }
    } catch (error) {
      console.error('Signup error:', error);
      if (error.response?.status === 409) {
        setSignupError('An account with this email already exists.');
      } else {
        setSignupError('An error occurred. Please try again.');
      }
    } finally {
      setSignupLoading(false);
    }
  };

  const handleBackToRegistration = () => {
    setStep(1);
    setSignupError('');
  };

  const handleOTPDeliverySelection = async (deliveryMethod) => {
    setSignupLoading(true);
    setSignupError('');

    try {
      const result = await sendOTP(signupFormData.email, signupFormData.phoneNumber, deliveryMethod);
      
      if (result.success) {
        toast.success(`OTP sent via ${deliveryMethod}!`);
        setStep(3); // Move to OTP verification
      } else {
        setSignupError(result.message || 'Failed to send OTP. Please try again.');
      }
    } catch (error) {
      console.error('OTP send error:', error);
      setSignupError('An error occurred. Please try again.');
    } finally {
      setSignupLoading(false);
    }
  };

  // Forgot Password Handlers
  const handleForgotPasswordClick = () => {
    setIsForgotPasswordMode(true);
    setForgotPasswordStep(1);
    setForgotPasswordError('');
    setForgotPasswordEmail('');
    setForgotPasswordPhone('');
  };

  const handleForgotPasswordBack = () => {
    setIsForgotPasswordMode(false);
    setForgotPasswordStep(1);
    setForgotPasswordError('');
    setForgotPasswordEmail('');
    setForgotPasswordPhone('');
  };

  const handleForgotPasswordIdentifierSubmit = async (e) => {
    e.preventDefault();
    setForgotPasswordLoading(true);
    setForgotPasswordError('');

    // Check if either email or phone is provided
    const hasEmail = forgotPasswordEmail && forgotPasswordEmail.trim();
    const hasPhone = forgotPasswordPhone && forgotPasswordPhone.trim();
    
    if (!hasEmail && !hasPhone) {
      setForgotPasswordError('Please enter either email or phone number');
      setForgotPasswordLoading(false);
      return;
    }

    // If email is provided, validate it
    if (hasEmail && !validateEmail(forgotPasswordEmail)) {
      setForgotPasswordError('Please enter a valid email address');
      setForgotPasswordLoading(false);
      return;
    }

    try {
      // Use email if provided, otherwise use phone
      const identifier = hasEmail ? forgotPasswordEmail : forgotPasswordPhone;
      const result = await forgotPasswordRequest(identifier);
      
      if (result.success) {
        // Store the found user's email and phone
        const foundEmail = result.data.email;
        const foundPhone = result.data.phoneNumber || '';
        
        setForgotPasswordEmail(foundEmail);
        setForgotPasswordPhone(foundPhone);
        
        // If user has both email and phone, go to delivery selection
        // If only email, auto send via email
        // If only phone, auto send via whatsapp
        if (foundEmail && foundPhone) {
          setForgotPasswordStep(2); // Show delivery method selection
        } else if (foundEmail) {
          // Auto send via email
          const otpResult = await sendPasswordResetOTP(foundEmail, null, 'email');
          if (otpResult.success) {
            toast.success('OTP sent via email!');
            setForgotPasswordStep(3);
          } else {
            setForgotPasswordError(otpResult.message);
          }
        } else if (foundPhone) {
          // Auto send via whatsapp
          const otpResult = await sendPasswordResetOTP(foundEmail, foundPhone, 'whatsapp');
          if (otpResult.success) {
            toast.success('OTP sent via WhatsApp!');
            setForgotPasswordStep(3);
          } else {
            setForgotPasswordError(otpResult.message);
          }
        }
      } else {
        setForgotPasswordError(result.message);
      }
    } catch (error) {
      console.error('Forgot password request error:', error);
      setForgotPasswordError('An error occurred. Please try again.');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleForgotPasswordOTPDelivery = async (deliveryMethod) => {
    setForgotPasswordLoading(true);
    setForgotPasswordError('');

    try {
      const result = await sendPasswordResetOTP(
        forgotPasswordEmail, 
        forgotPasswordPhone, 
        deliveryMethod
      );
      
      if (result.success) {
        toast.success(`OTP sent via ${deliveryMethod}!`);
        setForgotPasswordDeliveryMethod(deliveryMethod);
        setForgotPasswordStep(3); // Move to OTP verification
      } else {
        setForgotPasswordError(result.message);
      }
    } catch (error) {
      console.error('Send OTP error:', error);
      setForgotPasswordError('An error occurred. Please try again.');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleForgotPasswordOTPChange = (index, value) => {
    if (value.length > 1) return;
    
    const newOtp = [...forgotPasswordOTP];
    newOtp[index] = value;
    setForgotPasswordOTP(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`forgot-otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleForgotPasswordOTPKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !forgotPasswordOTP[index] && index > 0) {
      const prevInput = document.getElementById(`forgot-otp-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleForgotPasswordOTPPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '');
    if (pastedData.length === 6) {
      const newOtp = pastedData.split('');
      setForgotPasswordOTP(newOtp);
      const lastInput = document.getElementById('forgot-otp-5');
      if (lastInput) lastInput.focus();
    }
  };

  const handleForgotPasswordOTPSubmit = async (e) => {
    e.preventDefault();
    
    const otpString = forgotPasswordOTP.join('');
    if (otpString.length !== 6) {
      toast.error('Please enter the complete 6-digit OTP');
      return;
    }

    // Move to password step (OTP will be verified when submitting password)
    setForgotPasswordStep(4);
  };

  const handleResetPasswordSubmit = async (e) => {
    e.preventDefault();
    setForgotPasswordLoading(true);
    setForgotPasswordError('');

    // Validate passwords
    if (forgotPasswordNewPassword !== forgotPasswordConfirmPassword) {
      setForgotPasswordError('Passwords do not match');
      setForgotPasswordLoading(false);
      return;
    }

    if (!validatePassword(forgotPasswordNewPassword)) {
      setForgotPasswordError('Password must be at least 6 characters');
      setForgotPasswordLoading(false);
      return;
    }

    try {
      const otpString = forgotPasswordOTP.join('');
      
      // Call resetPassword with the OTP and new password
      // The backend will verify OTP and update password in one go
      const result = await resetPassword(forgotPasswordEmail, otpString, forgotPasswordNewPassword);
      
      if (result.success) {
        toast.success('Password reset successfully! You can now login.');
        // Reset and return to login
        setIsForgotPasswordMode(false);
        setForgotPasswordStep(1);
        setForgotPasswordEmail('');
        setForgotPasswordPhone('');
        setForgotPasswordOTP(['', '', '', '', '', '']);
        setForgotPasswordNewPassword('');
        setForgotPasswordConfirmPassword('');
      } else {
        setForgotPasswordError(result.message);
        // Go back to OTP step
        setForgotPasswordStep(3);
      }
    } catch (error) {
      console.error('Reset password error:', error);
      setForgotPasswordError('An error occurred. Please try again.');
      // Go back to OTP step
      setForgotPasswordStep(3);
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  // Show Forgot Password UI if in forgot password mode
  if (isForgotPasswordMode) {
    if (forgotPasswordStep === 1) {
      // Step 1: Enter identifier (email or phone)
      return (
        <PageContainer darkMode={darkMode}>
          <ThemeToggle onClick={toggleDarkMode} darkMode={darkMode} />
          <BrandingSection darkMode={darkMode}>
            <LogoContainer>
              <img 
                src="/skyone-logo-no-bg.png" 
                alt="SkyOne" 
              />
            </LogoContainer>
            <BrandingContent darkMode={darkMode}>
              <h1>Reset Password</h1>
              <p>Enter either your email or phone number to receive a verification code</p>
            </BrandingContent>
          </BrandingSection>

          <FormSection darkMode={darkMode}>
            <FormCard darkMode={darkMode}>
              <StyledForm onSubmit={handleForgotPasswordIdentifierSubmit} style={{ backgroundColor: darkMode ? '#0f0a14' : '#fafafa' }}>
                <StyledTitle darkMode={darkMode}>Enter Your Email or Phone</StyledTitle>
                <p style={{ fontSize: '0.875rem', color: darkMode ? '#94a3b8' : '#64748b', marginBottom: '1rem', textAlign: 'center' }}>
                  Enter either email or phone number
                </p>
                <StyledInput
                  darkMode={darkMode}
                  type="email"
                  placeholder="Email"
                  value={forgotPasswordEmail}
                  onChange={(e) => {
                    setForgotPasswordEmail(e.target.value);
                    // If email is entered, clear phone
                    if (e.target.value.trim()) {
                      setForgotPasswordPhone('');
                    }
                  }}
                  disabled={!!forgotPasswordPhone}
                />
                <div style={{ width: '100%' }}>
                  <PhoneInput
                    international
                    defaultCountry="US"
                    value={forgotPasswordPhone}
                    onChange={(value) => {
                      setForgotPasswordPhone(value || '');
                      // If phone is entered, clear email
                      if (value) {
                        setForgotPasswordEmail('');
                      }
                    }}
                    disabled={!!forgotPasswordEmail}
                    style={{
                      '--PhoneInput-color--focus': '#5d32b0',
                    }}
                    className={`phone-input-${darkMode ? 'dark' : 'light'}`}
                    placeholder="Phone Number"
                  />
                  <style>{`
                  .phone-input-dark, .phone-input-light {
                    width: 100%;
                    position: relative;
                  }
                  .phone-input-dark .PhoneInputCountry,
                  .phone-input-light .PhoneInputCountry {
                    position: absolute !important;
                    left: 15px !important;
                    top: 50% !important;
                    transform: translateY(-50%) !important;
                    z-index: 1 !important;
                    display: flex !important;
                    align-items: center !important;
                  }
                  .phone-input-dark input,
                  .phone-input-dark .PhoneInputInput {
                    background-color: rgba(255, 255, 255, 0.06) !important;
                    color: #fafafa !important;
                    border: 1px solid rgba(255, 255, 255, 0.12) !important;
                    border-radius: calc(0.625rem + 4px) !important;
                    padding: 12px 15px 12px 48px !important;
                    margin: 8px 0 !important;
                    width: 100% !important;
                    font-size: 1rem !important;
                    transition: all 0.3s ease !important;
                  }
                  .phone-input-light input,
                  .phone-input-light .PhoneInputInput {
                    background-color: #f4f4f5 !important;
                    color: #18181b !important;
                    border: 1px solid rgba(24, 24, 27, 0.08) !important;
                    border-radius: calc(0.625rem + 4px) !important;
                    padding: 12px 15px 12px 48px !important;
                    margin: 8px 0 !important;
                    width: 100% !important;
                    font-size: 1rem !important;
                    transition: all 0.3s ease !important;
                  }
                  .phone-input-dark input:focus,
                  .phone-input-dark .PhoneInputInput:focus {
                    outline: none !important;
                    border-color: #64748b !important;
                    box-shadow: 0 0 0 2px rgba(100, 116, 139, 0.35) !important;
                  }
                  .phone-input-light input:focus,
                  .phone-input-light .PhoneInputInput:focus {
                    outline: none !important;
                    border-color: #5d32b0 !important;
                    box-shadow: 0 0 0 2px rgba(93, 50, 176, 0.22) !important;
                  }
                  .phone-input-light.PhoneInput {
                    background: transparent !important;
                  }
                  .phone-input-dark .PhoneInputCountryIcon--border,
                  .phone-input-light .PhoneInputCountryIcon--border {
                    border-radius: 4px !important;
                  }
                  .phone-input-dark .PhoneInputCountryIcon img {
                    border-radius: 4px !important;
                  }
                  .phone-input-light .PhoneInputCountryIcon img {
                    border-radius: 4px !important;
                  }
                  .phone-input-dark .PhoneInputCountrySelect {
                    background-color: transparent !important;
                    padding: 4px 8px !important;
                    border: none !important;
                    border-radius: 6px !important;
                    color: #fafafa !important;
                  }
                  .phone-input-light .PhoneInputCountrySelect {
                    background-color: transparent !important;
                    padding: 4px 8px !important;
                    border: none !important;
                    border-radius: 6px !important;
                    color: #18181b !important;
                  }
                  .phone-input-dark input:disabled,
                  .phone-input-dark .PhoneInputInput:disabled,
                  .phone-input-light input:disabled,
                  .phone-input-light .PhoneInputInput:disabled {
                    opacity: 0.5 !important;
                    cursor: not-allowed !important;
                  }
                  `}</style>
                </div>
                {forgotPasswordError && <ErrorMessage>{forgotPasswordError}</ErrorMessage>}
                <StyledButton $darkMode={darkMode} type="submit" disabled={forgotPasswordLoading}>
                  {forgotPasswordLoading ? <LoadingSpinner /> : 'Continue'}
                </StyledButton>
                <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                  <button
                    type="button"
                    onClick={handleForgotPasswordBack}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: darkMode ? '#a9d045' : '#5d32b0',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      textDecoration: 'underline'
                    }}
                  >
                    ← Back to Sign In
                  </button>
                </div>
              </StyledForm>
            </FormCard>
          </FormSection>
        </PageContainer>
      );
    } else if (forgotPasswordStep === 2) {
      // Step 2: Select delivery method (if user has both email and phone)
      return (
        <PageContainer darkMode={darkMode}>
          <ThemeToggle onClick={toggleDarkMode} darkMode={darkMode} />
          <BrandingSection darkMode={darkMode}>
            <LogoContainer>
              <img 
                src="/skyone-logo-no-bg.png" 
                alt="SkyOne" 
              />
            </LogoContainer>
            <BrandingContent darkMode={darkMode}>
              <h1>Choose Delivery Method</h1>
              <p>Select how you'd like to receive your verification code</p>
            </BrandingContent>
          </BrandingSection>

          <FormSection darkMode={darkMode}>
            <FormCard darkMode={darkMode}>
              <StyledForm style={{ backgroundColor: darkMode ? '#0f0a14' : '#fafafa' }}>
                <StyledTitle darkMode={darkMode}>Select Delivery Method</StyledTitle>
                
                <div style={{ marginBottom: '2rem' }}>
                  <p style={{ marginBottom: '1rem', color: darkMode ? '#cbd5e1' : '#475569' }}>
                    Email: {forgotPasswordEmail}
                  </p>
                  {forgotPasswordPhone && (
                    <p style={{ color: darkMode ? '#cbd5e1' : '#475569' }}>
                      Phone: {forgotPasswordPhone}
                    </p>
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => handleForgotPasswordOTPDelivery('email')}
                  disabled={forgotPasswordLoading}
                  style={{
                    padding: '12px 45px',
                    backgroundColor: forgotPasswordLoading ? '#C5221F' : '#EA4335',
                    color: '#ffffff',
                    border: '1px solid #EA4335',
                    borderRadius: '25px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    cursor: forgotPasswordLoading ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease',
                    opacity: forgotPasswordLoading ? 0.6 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    position: 'relative',
                    overflow: 'hidden',
                    marginBottom: '1rem',
                    width: '100%'
                  }}
                  onMouseOver={(e) => {
                    if (!forgotPasswordLoading) {
                      e.target.style.backgroundColor = '#C5221F';
                      e.target.style.borderColor = '#C5221F';
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 8px 16px rgba(234, 67, 53, 0.3)';
                    }
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = '#EA4335';
                    e.target.style.borderColor = '#EA4335';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }}
                  onMouseDown={(e) => {
                    e.target.style.transform = 'translateY(0) scale(0.98)';
                  }}
                  onMouseUp={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                    <path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z" fill="currentColor"/>
                  </svg>
                  Send to Email
                </button>

                {forgotPasswordPhone && (
                  <button
                    type="button"
                    onClick={() => handleForgotPasswordOTPDelivery('whatsapp')}
                    disabled={forgotPasswordLoading}
                    style={{
                      padding: '12px 45px',
                      backgroundColor: forgotPasswordLoading ? '#128c7e' : '#25d366',
                      color: '#ffffff',
                      border: '1px solid #25d366',
                      borderRadius: '25px',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      letterSpacing: '1px',
                      textTransform: 'uppercase',
                      cursor: forgotPasswordLoading ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s ease',
                      opacity: forgotPasswordLoading ? 0.6 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      position: 'relative',
                      overflow: 'hidden',
                      width: '100%'
                    }}
                    onMouseOver={(e) => {
                      if (!forgotPasswordLoading) {
                        e.target.style.backgroundColor = '#128c7e';
                        e.target.style.borderColor = '#128c7e';
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 8px 16px rgba(37, 211, 102, 0.3)';
                      }
                    }}
                    onMouseOut={(e) => {
                      e.target.style.backgroundColor = '#25d366';
                      e.target.style.borderColor = '#25d366';
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = 'none';
                    }}
                    onMouseDown={(e) => {
                      e.target.style.transform = 'translateY(0) scale(0.98)';
                    }}
                    onMouseUp={(e) => {
                      e.target.style.transform = 'translateY(-2px)';
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" fill="currentColor"/>
                    </svg>
                    SEND TO WHATSAPP
                  </button>
                )}

                {forgotPasswordError && <ErrorMessage>{forgotPasswordError}</ErrorMessage>}

                <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                  <button
                    type="button"
                    onClick={() => setForgotPasswordStep(1)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: darkMode ? '#a9d045' : '#5d32b0',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      textDecoration: 'underline'
                    }}
                  >
                    ← Back
                  </button>
                </div>
              </StyledForm>
            </FormCard>
          </FormSection>
        </PageContainer>
      );
    } else if (forgotPasswordStep === 3) {
      // Step 3: Enter OTP
      return (
        <PageContainer darkMode={darkMode}>
          <ThemeToggle onClick={toggleDarkMode} darkMode={darkMode} />
          <BrandingSection darkMode={darkMode}>
            <LogoContainer>
              <img 
                src="/skyone-logo-no-bg.png" 
                alt="SkyOne" 
              />
            </LogoContainer>
            <BrandingContent darkMode={darkMode}>
              <h1>Verify Your Code</h1>
              <p>Enter the 6-digit code sent {forgotPasswordDeliveryMethod === 'email' ? 'to your email' : 'via WhatsApp'}</p>
              <p style={{ color: darkMode ? '#a9d045' : '#5d32b0', fontWeight: 600, marginTop: '0.5rem' }}>
                {forgotPasswordDeliveryMethod === 'email' ? forgotPasswordEmail : forgotPasswordPhone}
              </p>
            </BrandingContent>
          </BrandingSection>

          <FormSection darkMode={darkMode}>
            <FormCard darkMode={darkMode}>
              <StyledForm onSubmit={handleForgotPasswordOTPSubmit} style={{ backgroundColor: darkMode ? '#0f0a14' : '#fafafa' }}>
                <StyledTitle darkMode={darkMode}>Enter Verification Code</StyledTitle>
                
                <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', margin: '2rem 0' }}>
                  {forgotPasswordOTP.map((digit, index) => (
                    <input
                      key={index}
                      id={`forgot-otp-${index}`}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength="1"
                      value={digit}
                      onChange={(e) => handleForgotPasswordOTPChange(index, e.target.value)}
                      onKeyDown={(e) => handleForgotPasswordOTPKeyDown(index, e)}
                      onPaste={handleForgotPasswordOTPPaste}
                      style={{
                        width: '3rem',
                        height: '3rem',
                        textAlign: 'center',
                        fontSize: '1.5rem',
                        fontWeight: 'bold',
                        border: `2px solid ${darkMode ? '#475569' : '#e5e7eb'}`,
                        borderRadius: '12px',
                        backgroundColor: darkMode ? '#334155' : '#f9fafb',
                        color: darkMode ? '#f1f5f9' : '#1e293b',
                        transition: 'all 0.3s ease'
                      }}
                      autoComplete="off"
                    />
                  ))}
                </div>

                <StyledButton $darkMode={darkMode} type="submit" disabled={forgotPasswordLoading || forgotPasswordOTP.join('').length !== 6}>
                  {forgotPasswordLoading ? 'Verifying...' : 'Verify Code'}
                </StyledButton>

                {forgotPasswordError && <ErrorMessage>{forgotPasswordError}</ErrorMessage>}

                <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                  <button
                    type="button"
                    onClick={() => setForgotPasswordStep(forgotPasswordEmail && forgotPasswordPhone ? 2 : 1)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: darkMode ? '#a9d045' : '#5d32b0',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      textDecoration: 'underline'
                    }}
                  >
                    ← Back
                  </button>
                </div>
              </StyledForm>
            </FormCard>
          </FormSection>
        </PageContainer>
      );
    } else if (forgotPasswordStep === 4) {
      // Step 4: Enter new password
      return (
        <PageContainer darkMode={darkMode}>
          <ThemeToggle onClick={toggleDarkMode} darkMode={darkMode} />
          <BrandingSection darkMode={darkMode}>
            <LogoContainer>
              <img 
                src="/skyone-logo-no-bg.png" 
                alt="SkyOne" 
              />
            </LogoContainer>
            <BrandingContent darkMode={darkMode}>
              <h1>Create New Password</h1>
              <p>Enter your new password</p>
            </BrandingContent>
          </BrandingSection>

          <FormSection darkMode={darkMode}>
            <FormCard darkMode={darkMode}>
              <StyledForm onSubmit={handleResetPasswordSubmit} style={{ backgroundColor: darkMode ? '#0f0a14' : '#fafafa' }}>
                <StyledTitle darkMode={darkMode}>New Password</StyledTitle>
                
                <div style={{ position: 'relative', width: '100%' }}>
                  <StyledInput
                    darkMode={darkMode}
                    type={showForgotPasswordNewPassword ? 'text' : 'password'}
                    placeholder="New Password"
                    value={forgotPasswordNewPassword}
                    onChange={(e) => setForgotPasswordNewPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowForgotPasswordNewPassword(!showForgotPasswordNewPassword)}
                    style={{
                      position: 'absolute',
                      right: '15px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#64748b'
                    }}
                  >
                    {showForgotPasswordNewPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                  </button>
                </div>

                <div style={{ position: 'relative', width: '100%' }}>
                  <StyledInput
                    darkMode={darkMode}
                    type={showForgotPasswordConfirmPassword ? 'text' : 'password'}
                    placeholder="Confirm New Password"
                    value={forgotPasswordConfirmPassword}
                    onChange={(e) => setForgotPasswordConfirmPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowForgotPasswordConfirmPassword(!showForgotPasswordConfirmPassword)}
                    style={{
                      position: 'absolute',
                      right: '15px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#64748b'
                    }}
                  >
                    {showForgotPasswordConfirmPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                  </button>
                </div>

                {forgotPasswordError && <ErrorMessage>{forgotPasswordError}</ErrorMessage>}
                <StyledButton $darkMode={darkMode} type="submit" disabled={forgotPasswordLoading}>
                  {forgotPasswordLoading ? <LoadingSpinner /> : 'Reset Password'}
                </StyledButton>

                <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                  <button
                    type="button"
                    onClick={() => setForgotPasswordStep(3)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: darkMode ? '#a9d045' : '#5d32b0',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      textDecoration: 'underline'
                    }}
                  >
                    ← Back
                  </button>
                </div>
              </StyledForm>
            </FormCard>
          </FormSection>
        </PageContainer>
      );
    }
  }

  // Show OTP delivery selection if step is 2
  if (step === 2) {
    return (
      <PageContainer darkMode={darkMode}>
        <ThemeToggle onClick={toggleDarkMode} darkMode={darkMode} />
        <BrandingSection darkMode={darkMode}>
          <LogoContainer>
            <img 
              src="/skyone-logo-no-bg.png" 
              alt="SkyOne" 
            />
          </LogoContainer>
          <BrandingContent darkMode={darkMode}>
            <h1>Choose Delivery Method</h1>
            <p>Select how you'd like to receive your verification code</p>
          </BrandingContent>
        </BrandingSection>

        <FormSection darkMode={darkMode}>
          <FormCard darkMode={darkMode}>
            <StyledForm>
              <StyledTitle darkMode={darkMode}>Select Delivery Method</StyledTitle>
              
              <div style={{ marginBottom: '2rem' }}>
                <p style={{ marginBottom: '1rem', color: darkMode ? '#cbd5e1' : '#475569' }}>
                  Email: {signupFormData.email}
                </p>
                {signupFormData.phoneNumber && (
                  <p style={{ color: darkMode ? '#cbd5e1' : '#475569' }}>
                    Phone: {signupFormData.phoneNumber}
                  </p>
                )}
              </div>

              <button
                type="button"
                onClick={() => handleOTPDeliverySelection('email')}
                disabled={signupLoading}
                style={{
                  padding: '12px 45px',
                  backgroundColor: signupLoading ? '#C5221F' : '#EA4335',
                  color: '#ffffff',
                  border: '1px solid #EA4335',
                  borderRadius: '25px',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  cursor: signupLoading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  opacity: signupLoading ? 0.6 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  position: 'relative',
                  overflow: 'hidden',
                  marginBottom: '1rem',
                  width: '100%'
                }}
                onMouseOver={(e) => {
                  if (!signupLoading) {
                    e.target.style.backgroundColor = '#C5221F';
                    e.target.style.borderColor = '#C5221F';
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 8px 16px rgba(234, 67, 53, 0.3)';
                  }
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = '#EA4335';
                  e.target.style.borderColor = '#EA4335';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }}
                onMouseDown={(e) => {
                  e.target.style.transform = 'translateY(0) scale(0.98)';
                }}
                onMouseUp={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                  <path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z" fill="currentColor"/>
                </svg>
                Send to Email
              </button>

              {signupFormData.phoneNumber && (
                <button
                  type="button"
                  onClick={() => handleOTPDeliverySelection('whatsapp')}
                  disabled={signupLoading}
                  style={{
                    padding: '12px 45px',
                    backgroundColor: signupLoading ? '#128c7e' : '#25d366',
                    color: '#ffffff',
                    border: '1px solid #25d366',
                    borderRadius: '25px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    cursor: signupLoading ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease',
                    opacity: signupLoading ? 0.6 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    position: 'relative',
                    overflow: 'hidden',
                    width: '100%'
                  }}
                  onMouseOver={(e) => {
                    if (!signupLoading) {
                      e.target.style.backgroundColor = '#128c7e';
                      e.target.style.borderColor = '#128c7e';
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 8px 16px rgba(37, 211, 102, 0.3)';
                    }
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = '#25d366';
                    e.target.style.borderColor = '#25d366';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }}
                  onMouseDown={(e) => {
                    e.target.style.transform = 'translateY(0) scale(0.98)';
                  }}
                  onMouseUp={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" fill="currentColor"/>
                  </svg>
                  SEND TO WHATSAPP
                </button>
              )}

              {signupError && <ErrorMessage>{signupError}</ErrorMessage>}

              <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                <button
                  type="button"
                  onClick={handleBackToRegistration}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: darkMode ? '#a9d045' : '#5d32b0',
                    cursor: 'pointer',
                    textDecoration: 'underline'
                  }}
                >
                  ← Back to registration
                </button>
              </div>
            </StyledForm>
          </FormCard>
        </FormSection>
      </PageContainer>
    );
  }

  // Show OTP verification if step is 3
  if (step === 3) {
    return <OTPVerification userData={signupFormData} onBack={() => setStep(2)} />;
  }

  return (
    <PageContainer darkMode={darkMode}>
      <ThemeToggle onClick={toggleDarkMode} darkMode={darkMode} />
      <BrandingSection darkMode={darkMode}>
        <LogoContainer>
          <img 
            src="/skyone-logo-no-bg.png" 
            alt="SkyOne" 
          />
        </LogoContainer>
        <BrandingContent darkMode={darkMode}>
          <h1>
            <span style={{ color: darkMode ? "#ffffff" : "#3d2a5c" }}>Sky</span>
            <span style={{ color: "#c2e858" }}>One </span>
            <span style={{ color: darkMode ? "#ffffff" : "#3d2a5c" }}>
              VIP Services
            </span>
          </h1>
          <p>Professional airport services and a premium customer experience</p>
          <FeatureList>
            <FeatureItem darkMode={darkMode}>
              <div className="icon">
                <svg className={`h-6 w-6 ${darkMode ? "text-[#a9d045]" : "text-[#5d32b0]"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div className="text">
                <div className="title">Secure Platform</div>
                <div className="description">Enterprise security</div>
              </div>
            </FeatureItem>
            <FeatureItem darkMode={darkMode}>
              <div className="icon">
                <svg className={`h-6 w-6 ${darkMode ? "text-[#a9d045]" : "text-[#5d32b0]"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="text">
                <div className="title">24/7 Availability</div>
                <div className="description">Always available</div>
              </div>
            </FeatureItem>
            <FeatureItem darkMode={darkMode}>
              <div className="icon">
                <svg className={`h-6 w-6 ${darkMode ? "text-[#a9d045]" : "text-[#5d32b0]"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              </div>
              <div className="text">
                <div className="title">Premium Service</div>
                <div className="description">Excellence guaranteed</div>
              </div>
            </FeatureItem>
          </FeatureList>
        </BrandingContent>
      </BrandingSection>

      <FormSection darkMode={darkMode}>
        <FormCard darkMode={darkMode}>
          <SignInContainer signingIn={signingIn}>
            <StyledForm onSubmit={handleLoginSubmit} style={{ backgroundColor: darkMode ? '#0f0a14' : '#fafafa' }}>
              <StyledTitle darkMode={darkMode}>Sign in</StyledTitle>
              <p style={{ fontSize: '0.875rem', color: darkMode ? '#94a3b8' : '#64748b', marginBottom: '1rem', textAlign: 'center' }}>
                Enter either email or phone number
              </p>
              <StyledInput
                darkMode={darkMode}
                type="email"
                name="email"
                placeholder="Email"
                value={loginFormData.email}
                onChange={(e) => {
                  handleLoginChange(e);
                  // If email is entered, clear phone
                  if (e.target.value.trim()) {
                    setLoginFormData(prev => ({ ...prev, phoneNumber: '' }));
                  }
                }}
                disabled={!!loginFormData.phoneNumber}
              />
              <div style={{ width: '100%' }}>
                <PhoneInput
                  international
                  defaultCountry="US"
                  value={loginFormData.phoneNumber}
                  onChange={(value) => {
                    setLoginFormData(prev => ({ ...prev, phoneNumber: value || '' }));
                    // If phone is entered, clear email
                    if (value) {
                      setLoginFormData(prev => ({ ...prev, email: '' }));
                    }
                  }}
                  disabled={!!loginFormData.email}
                  style={{
                    '--PhoneInput-color--focus': '#5d32b0',
                  }}
                  className={`phone-input-${darkMode ? 'dark' : 'light'}`}
                  placeholder="Phone Number"
                />
                <style>{`
                .phone-input-dark, .phone-input-light {
                  width: 100%;
                  position: relative;
                }
                .phone-input-dark input,
                .phone-input-dark .PhoneInputInput {
                  background-color: rgba(255, 255, 255, 0.06) !important;
                  color: #fafafa !important;
                  border: 1px solid rgba(255, 255, 255, 0.12) !important;
                  border-radius: calc(0.625rem + 4px) !important;
                  padding: 12px 15px 12px 48px !important;
                  margin: 8px 0 !important;
                  width: 100% !important;
                  font-size: 1rem !important;
                  transition: all 0.3s ease !important;
                }
                .phone-input-light input,
                .phone-input-light .PhoneInputInput {
                  background-color: #f4f4f5 !important;
                  color: #18181b !important;
                  border: 1px solid rgba(24, 24, 27, 0.08) !important;
                  border-radius: calc(0.625rem + 4px) !important;
                  padding: 12px 15px 12px 48px !important;
                  margin: 8px 0 !important;
                  width: 100% !important;
                  font-size: 1rem !important;
                  transition: all 0.3s ease !important;
                }
                .phone-input-dark input:focus,
                .phone-input-dark .PhoneInputInput:focus {
                  outline: none !important;
                  border-color: #64748b !important;
                  box-shadow: 0 0 0 2px rgba(100, 116, 139, 0.35) !important;
                }
                .phone-input-light input:focus,
                .phone-input-light .PhoneInputInput:focus {
                  outline: none !important;
                  border-color: #5d32b0 !important;
                  box-shadow: 0 0 0 2px rgba(93, 50, 176, 0.22) !important;
                }
                .phone-input-light.PhoneInput {
                  background: transparent !important;
                }
                .phone-input-dark .PhoneInputCountry,
                .phone-input-light .PhoneInputCountry {
                  position: absolute !important;
                  left: 15px !important;
                  top: 50% !important;
                  transform: translateY(-50%) !important;
                  z-index: 1 !important;
                  display: flex !important;
                  align-items: center !important;
                }
                .phone-input-dark .PhoneInputCountryIcon--border,
                .phone-input-light .PhoneInputCountryIcon--border {
                  border-radius: 4px !important;
                }
                .phone-input-dark .PhoneInputCountryIcon img {
                  border-radius: 4px !important;
                }
                .phone-input-light .PhoneInputCountryIcon img {
                  border-radius: 4px !important;
                }
                .phone-input-dark .PhoneInputCountrySelect {
                  background-color: transparent !important;
                  padding: 4px 8px !important;
                  border: none !important;
                  border-radius: 6px !important;
                  color: #fafafa !important;
                }
                .phone-input-light .PhoneInputCountrySelect {
                  background-color: transparent !important;
                  padding: 4px 8px !important;
                  border: none !important;
                  border-radius: 6px !important;
                  color: #18181b !important;
                }
                `}</style>
              </div>
              <div style={{ position: 'relative', width: '100%' }}>
                <StyledInput
                  darkMode={darkMode}
                  type={showLoginPassword ? 'text' : 'password'}
                  name="password"
                  placeholder="Password"
                  value={loginFormData.password}
                  onChange={handleLoginChange}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowLoginPassword(!showLoginPassword)}
                  style={{
                    position: 'absolute',
                    right: '15px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#64748b'
                  }}
                >
                  {showLoginPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                </button>
              </div>
              {loginError && <ErrorMessage>{loginError}</ErrorMessage>}
              <StyledButton $darkMode={darkMode} type="submit" disabled={loginLoading}>
                {loginLoading ? <LoadingSpinner /> : 'Sign In'}
              </StyledButton>
              <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                <button
                  type="button"
                  onClick={handleForgotPasswordClick}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: darkMode ? '#a9d045' : '#5d32b0',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    textDecoration: 'underline'
                  }}
                >
                  Forgot Password?
                </button>
              </div>
            </StyledForm>
          </SignInContainer>

          <SignUpContainer signingIn={signingIn}>
            <StyledForm onSubmit={handleSignupSubmit} style={{ backgroundColor: darkMode ? '#0f0a14' : '#fafafa' }}>
              <StyledTitle darkMode={darkMode}>Create Account</StyledTitle>
              <StyledInput
                darkMode={darkMode}
                type="text"
                name="firstName"
                placeholder="First Name"
                value={signupFormData.firstName}
                onChange={handleSignupChange}
                required
              />
              <StyledInput
                darkMode={darkMode}
                type="text"
                name="lastName"
                placeholder="Last Name"
                value={signupFormData.lastName}
                onChange={handleSignupChange}
                required
              />
              <StyledInput
                darkMode={darkMode}
                type="email"
                name="email"
                placeholder="Email"
                value={signupFormData.email}
                onChange={handleSignupChange}
                required
              />
              <div style={{ width: '100%' }}>
                <PhoneInput
                  international
                  defaultCountry="US"
                  value={signupFormData.phoneNumber}
                  onChange={(value) => setSignupFormData(prev => ({ ...prev, phoneNumber: value || '' }))}
                  style={{
                    '--PhoneInput-color--focus': '#5d32b0',
                  }}
                  className={`phone-input-${darkMode ? 'dark' : 'light'}`}
                  placeholder="Phone Number"
                />
                <style>{`
                .phone-input-dark, .phone-input-light {
                  width: 100%;
                  position: relative;
                }
                .phone-input-dark input,
                .phone-input-dark .PhoneInputInput {
                  background-color: rgba(255, 255, 255, 0.06) !important;
                  color: #fafafa !important;
                  border: 1px solid rgba(255, 255, 255, 0.12) !important;
                  border-radius: calc(0.625rem + 4px) !important;
                  padding: 12px 15px 12px 48px !important;
                  margin: 8px 0 !important;
                  width: 100% !important;
                  font-size: 1rem !important;
                  transition: all 0.3s ease !important;
                }
                .phone-input-light input,
                .phone-input-light .PhoneInputInput {
                  background-color: #f4f4f5 !important;
                  color: #18181b !important;
                  border: 1px solid rgba(24, 24, 27, 0.08) !important;
                  border-radius: calc(0.625rem + 4px) !important;
                  padding: 12px 15px 12px 48px !important;
                  margin: 8px 0 !important;
                  width: 100% !important;
                  font-size: 1rem !important;
                  transition: all 0.3s ease !important;
                }
                .phone-input-dark input:focus,
                .phone-input-dark .PhoneInputInput:focus {
                  outline: none !important;
                  border-color: #64748b !important;
                  box-shadow: 0 0 0 2px rgba(100, 116, 139, 0.35) !important;
                }
                .phone-input-light input:focus,
                .phone-input-light .PhoneInputInput:focus {
                  outline: none !important;
                  border-color: #5d32b0 !important;
                  box-shadow: 0 0 0 2px rgba(93, 50, 176, 0.22) !important;
                }
                .phone-input-light.PhoneInput {
                  background: transparent !important;
                }
                .phone-input-dark .PhoneInputCountry,
                .phone-input-light .PhoneInputCountry {
                  position: absolute !important;
                  left: 15px !important;
                  top: 50% !important;
                  transform: translateY(-50%) !important;
                  z-index: 1 !important;
                  display: flex !important;
                  align-items: center !important;
                }
                .phone-input-dark .PhoneInputCountryIcon--border,
                .phone-input-light .PhoneInputCountryIcon--border {
                  border-radius: 4px !important;
                }
                .phone-input-dark .PhoneInputCountryIcon img {
                  border-radius: 4px !important;
                }
                .phone-input-light .PhoneInputCountryIcon img {
                  border-radius: 4px !important;
                }
                .phone-input-dark .PhoneInputCountrySelect {
                  background-color: transparent !important;
                  padding: 4px 8px !important;
                  border: none !important;
                  border-radius: 6px !important;
                  color: #fafafa !important;
                }
                .phone-input-light .PhoneInputCountrySelect {
                  background-color: transparent !important;
                  padding: 4px 8px !important;
                  border: none !important;
                  border-radius: 6px !important;
                  color: #18181b !important;
                }
                `}</style>
              </div>
              <div style={{ position: 'relative', width: '100%' }}>
                <StyledInput
                  darkMode={darkMode}
                  type={showSignupPassword ? 'text' : 'password'}
                  name="password"
                  placeholder="Password"
                  value={signupFormData.password}
                  onChange={handleSignupChange}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowSignupPassword(!showSignupPassword)}
                  style={{
                    position: 'absolute',
                    right: '15px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#64748b'
                  }}
                >
                  {showSignupPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                </button>
              </div>
              <div style={{ position: 'relative', width: '100%' }}>
                <StyledInput
                  darkMode={darkMode}
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  placeholder="Confirm Password"
                  value={signupFormData.confirmPassword}
                  onChange={handleSignupChange}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={{
                    position: 'absolute',
                    right: '15px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#64748b'
                  }}
                >
                  {showConfirmPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                </button>
              </div>
              {Object.keys(signupValidationErrors).length > 0 && (
                <ErrorMessage>
                  {Object.values(signupValidationErrors)[0]}
                </ErrorMessage>
              )}
              {signupError && <ErrorMessage>{signupError}</ErrorMessage>}
              <StyledButton $darkMode={darkMode} type="submit" disabled={signupLoading}>
                {signupLoading ? <LoadingSpinner /> : 'Sign Up'}
              </StyledButton>
            </StyledForm>
          </SignUpContainer>

          <OverlayContainer signingIn={signingIn}>
            <Overlay signingIn={signingIn}>
              <LeftOverlayPanel signingIn={signingIn}>
                <OverlayTitle>Welcome Back!</OverlayTitle>
                <StyledParagraph>
                  To keep connected with us please login with your personal info
                </StyledParagraph>
                <GhostButton $darkMode={darkMode} onClick={toggleForm}>
                  Sign In
                </GhostButton>
              </LeftOverlayPanel>
              <RightOverlayPanel signingIn={signingIn}>
                <OverlayTitle>Greetings!</OverlayTitle>
                <StyledParagraph>
                  Enter your personal details and start journey with us
                </StyledParagraph>
                <GhostButton $darkMode={darkMode} onClick={toggleForm}>
                  Sign Up
                </GhostButton>
                
                {/* Google Signup Button on Greeting Panel */}
                <div style={{ marginTop: '1.5rem', width: '100%' }}>
                  <GoogleOneTap setLoading={setSignupLoading} setError={setSignupError} />
                </div>
              </RightOverlayPanel>
            </Overlay>
          </OverlayContainer>
        </FormCard>
      </FormSection>
    </PageContainer>
  );
};

export default StyledAuth; 